/**
 * Jobs Service
 * Provides job list and detail operations
 */

import type { GatewayClient } from './client'
import type { JobResponse, JobsListResponse, ListJobsOptions } from './types'

// Re-export types for backward compatibility
export type { JobResponse, JobsListResponse, ListJobsOptions }

// ============================================================================
// Service Interface
// ============================================================================

export interface JobsService {
  /**
   * List jobs for the current project
   */
  list(options?: ListJobsOptions): Promise<JobsListResponse>

  /**
   * Get job details by ID
   */
  get(jobId: string): Promise<JobResponse>
}

// ============================================================================
// Options
// ============================================================================

export interface ProjectJobsOptions {
  projectId: string
}

// ============================================================================
// Implementation
// ============================================================================

/**
 * Create a project-scoped JobsService instance
 */
export function createJobsService(
  client: GatewayClient,
  options?: ProjectJobsOptions
): JobsService {
  const projectId = options?.projectId

  // Helper to build request options with projectId query parameter
  const withProjectParam = (
    additionalOptions?: Record<string, unknown>
  ): Record<string, unknown> => {
    if (!projectId) return additionalOptions || {}
    return {
      ...additionalOptions,
      params: {
        projectId,
        ...(additionalOptions?.params as Record<string, string>),
      },
    }
  }

  return {
    async list(options?: ListJobsOptions): Promise<JobsListResponse> {
      return client.get<JobsListResponse>(
        '/v1/jobs',
        withProjectParam({
          params: options as Record<
            string,
            string | number | boolean | undefined
          >,
        })
      )
    },

    async get(jobId: string): Promise<JobResponse> {
      return client.get<JobResponse>(`/v1/jobs/${jobId}`, withProjectParam())
    },
  }
}
